import { MerchantLayout } from '../../components/layouts/Layout'
import { useNotifications } from '../../hooks/useNotifications'
import { Card, Badge, Button } from '../../components/common/UI'
import { Bell, CheckCheck, Mail } from 'lucide-react'
import { formatters } from '../../utils/formatting'

export default function Notifications() {
  const { notifications, loading, unreadCount, markAsRead, markAllAsRead } = useNotifications()

  const getIcon = (type: string) => {
    switch (type) {
      case 'AD_APPROVAL':
      case 'AD_REJECTION':
        return Bell
      case 'TOKEN_PURCHASE':
        return CheckCheck
      default:
        return Mail
    }
  }

  const getVariant = (type: string) => {
    switch (type) {
      case 'AD_APPROVAL':
        return 'success'
      case 'AD_REJECTION':
        return 'error'
      case 'TOKEN_PURCHASE':
        return 'info'
      default:
        return 'default'
    }
  }

  if (loading) {
    return (
      <MerchantLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p>Loading notifications...</p>
        </div>
      </MerchantLayout>
    )
  }

  return (
    <MerchantLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Notifications</h1>
            <p className="text-gray-600">
              {unreadCount > 0 ? `${unreadCount} unread notifications` : 'All caught up!'}
            </p>
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" onClick={markAllAsRead} size="sm">
              Mark All as Read
            </Button>
          )}
        </div>

        <div className="space-y-3">
          {notifications.map((notification) => {
            const Icon = getIcon(notification.type)
            const variant = getVariant(notification.type)

            return (
              <Card
                key={notification.id}
                className={`${!notification.isRead ? 'border-l-4 border-l-blue-600' : ''}`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    variant === 'success' ? 'bg-green-100' :
                    variant === 'error' ? 'bg-red-100' :
                    variant === 'info' ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    <Icon className={`w-5 h-5 ${
                      variant === 'success' ? 'text-green-600' :
                      variant === 'error' ? 'text-red-600' :
                      variant === 'info' ? 'text-blue-600' : 'text-gray-600'
                    }`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{notification.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {formatters.relativeTime(notification.createdAt)}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={variant as any}>{notification.type.replace('_', ' ')}</Badge>
                        {!notification.isRead && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => markAsRead(notification.id)}
                          >
                            Mark Read
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>

        {notifications.length === 0 && (
          <Card className="text-center py-12">
            <Bell className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Notifications</h3>
            <p className="text-gray-600">You're all caught up!</p>
          </Card>
        )}
      </div>
    </MerchantLayout>
  )
}